import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import apiPublic from "../apiPublic";

const PublicSiteContext = createContext(null);
const CACHE_TTL_MS = 5 * 60 * 1000;

function getPublicCacheNamespace() {
  if (typeof window === "undefined") return "server";
  const host = String(window.location.hostname || "")
    .trim()
    .toLowerCase()
    .replace(/^www\./, "");
  return host || "default";
}

function getSettingsCacheKey() {
  return `takofy.public.settings.${getPublicCacheNamespace()}.v1`;
}

function getCompanyCacheKey() {
  return `takofy.public.company.${getPublicCacheNamespace()}.v2`;
}

const defaultSettings = {
  ContactPhone: "540 174 00 54",
  ContactPhoneLink: "tel:5401740054",
  ContactEmail: "",
  ContactAddress: "",
  ContactInstagram: "",
  ContactFacebook: "",
  ContactTwitter: "",
  ContactYoutube: "",
  ContactAirbnb: "",
  ContactWhatsApp: "",
  ContactInstagramHandle: "",
  ContactWhatsAppDefaultMessage: "Merhaba, bilgi almak istiyorum.",
  ContactWhatsAppHomeMessage: "Merhaba, ana sayfa uzerinden yaziyorum. Musaitlik ve fiyat bilgisi alabilir miyim?",
  ContactWhatsAppReservationMessage: "Merhaba, rezervasyon detaylari ve uygun tarihleri ogrenmek istiyorum.",
  ContactWhatsAppSupportMessage: "Merhaba, destek almak istiyorum. Musait oldugunuzda donus saglayabilir misiniz?",
  PolicyKvkk: "",
  PolicyPrivacy: "",
  PolicyCookie: "",
  HeroEyebrow: "Sapanca'da dogayla uyumlu kurumsal konaklama deneyimi",
  HeroTitleBody: "Dogayla temas eden, sakin ama duzenli bir konaklama deneyimini net iletisim ve hizli rezervasyon adimlariyla sunuyoruz.",
  HeroLead:
    "Ozel havuzlu villa konseptini, guven veren misafir deneyimi ve dogrudan rezervasyon kolayligi ile birlestiriyoruz. Kisa kacamaklar, aile konaklamalari ve planli hafta sonlari icin temiz, dengeli ve profesyonel bir akis sunuyoruz.",
  HeroBadge1: "Sapanca / Sakarya",
  HeroBadge2: "Dogrudan rezervasyon",
  HeroBadge3: "Hizli musaitlik sorgulama",
  HeroMetric1Title: "4 mevsim",
  HeroMetric1Text: "konforlu konaklama akisi",
  HeroMetric2Title: "Direkt",
  HeroMetric2Text: "telefon ve WhatsApp destegi",
  HeroMetric3Title: "Kurumsal",
  HeroMetric3Text: "net iletisim ve hizli rezervasyon",
  HeroPanelIntroTitle: "Sade, hizli ve guven veren bir rezervasyon deneyimi",
  HeroPanelIntroText:
    "Misafir iletisimi, rezervasyon akisi ve konaklama detaylarini tek noktadan netlestiriyoruz.",
  HeroPanelNoteTitle: "Konaklama Yaklasimi",
  HeroPanelNoteText:
    "Sessiz, temiz ve kolay yonlendirilen bir rezervasyon akisi kuruyoruz. Misafirlerimiz ihtiyac duydugunda WhatsApp ve telefonla hizli destek alabilir.",
  HeroStackImage1: "",
  HeroStackImage2: "",
  HeroStackImage3: "",
  HeroSliderEffect: "fade",
};

const defaultCompany = {
  Name: "",
  CityLine: "",
  Address: "",
  Email: "",
  Phone: "",
  LogoUrl: "",
  LicenceNo: "",
  AuthorizedPerson: "",
};

function loadCachedJson(key, fallback) {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (Date.now() - Number(parsed?.savedAt || 0) > CACHE_TTL_MS) {
      window.sessionStorage.removeItem(key);
      return fallback;
    }
    return parsed?.data ? { ...fallback, ...parsed.data } : fallback;
  } catch {
    return fallback;
  }
}

function saveCachedJson(key, data) {
  try {
    window.sessionStorage.setItem(
      key,
      JSON.stringify({
        savedAt: Date.now(),
        data,
      })
    );
  } catch {
    // no-op
  }
}

export function PublicSiteProvider({ children }) {
  const [settings, setSettings] = useState(() =>
    loadCachedJson(getSettingsCacheKey(), defaultSettings)
  );
  const [company, setCompany] = useState(() =>
    loadCachedJson(getCompanyCacheKey(), defaultCompany)
  );
  const [loading, setLoading] = useState(false);
  const lastLoadedAtRef = useRef(0);
  const inFlightRef = useRef(null);

  useEffect(() => {
    let alive = true;

    async function loadPublicSite() {
      if (inFlightRef.current) {
        await inFlightRef.current;
        return;
      }

      if (Date.now() - lastLoadedAtRef.current < 60000) {
        return;
      }

      const request = (async () => {
        try {
          setLoading(true);
          const [settingsRes, companyRes] = await Promise.allSettled([
            apiPublic.get("/settings/public"),
            apiPublic.get("/companies/public/default"),
          ]);

          if (!alive) return;

          if (settingsRes.status === "fulfilled" && Array.isArray(settingsRes.value.data)) {
            const nextSettings = { ...defaultSettings };
            settingsRes.value.data.forEach((row) => {
              if (row?.KeyName && row?.SettingValue) {
                nextSettings[row.KeyName] = row.SettingValue;
              }
            });
            setSettings(nextSettings);
            saveCachedJson(getSettingsCacheKey(), nextSettings);
          }

          if (companyRes.status === "fulfilled" && companyRes.value.data?.company) {
            const nextCompany = {
              ...defaultCompany,
              ...companyRes.value.data.company,
            };
            setCompany((prev) => ({
              ...prev,
              ...nextCompany,
            }));
            saveCachedJson(getCompanyCacheKey(), nextCompany);
          }

          lastLoadedAtRef.current = Date.now();
        } catch {
          // fall back to defaults
        } finally {
          inFlightRef.current = null;
          if (alive) setLoading(false);
        }
      })();

      inFlightRef.current = request;
      await request;
    }

    void loadPublicSite();

    return () => {
      alive = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      settings,
      company,
      loading,
    }),
    [company, loading, settings]
  );

  return <PublicSiteContext.Provider value={value}>{children}</PublicSiteContext.Provider>;
}

export function usePublicSite() {
  const value = useContext(PublicSiteContext);
  if (!value) {
    throw new Error("usePublicSite must be used within PublicSiteProvider");
  }
  return value;
}




